import type { FC } from "react"
import { useTranslation } from "react-i18next"
import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"

import {
  LIMITING_INTENSITY_THRESHOLD,
  getIntensity,
} from "helpers/utils/limitingSpanColor"
import { formatTimestamp } from "helpers/utils/dateUtils"
import AxisRuler from "./AxisRuler"
import GridCell from "./GridCell"
import LaneMarkerBand from "./LaneMarkerBand"
import SpanCell from "./SpanCell"
import {
  getLaneMarkers,
  LABEL_WIDTH,
  MARKER_BAND_HEIGHT,
  ROW_GAP,
  type HeatmapRow,
  type LaneMarkers,
} from "./helpers"

interface LaneStripsProps {
  rows: HeatmapRow[]
  // Which spans of the line the window is showing, in order.
  visibleIndexes: number[]
  indexBySpanId: Map<number, number>
  isNeutral: boolean
  maxPct: number
  rowHeight: number
  markerHitWidth: number
  markersByKey?: Partial<Record<"aar" | "dlr", LaneMarkers>>
  laneErrors?: Partial<Record<"aar" | "dlr", boolean>>
  // The span the read-out is on, and the pinned one. Both drive the cells.
  activeSpanIndex: number | null
  selectedSpanIndex: number | null
  colorFor: (intensity: number) => string
  // The grey the map's neutral route uses, so the two views agree on what
  // "just a span" looks like.
  route: string
  tickIndexes: Set<number>
  onEnter: (spanIndex: number, spanId: number | null) => void
  onLeave: () => void
  onSelect: (spanId: number | null) => void
}

/**
 * The lanes themselves: the method gutter, one strip of cells per method with
 * its marker band above it, and the axis under the last one.
 */
const LaneStrips: FC<LaneStripsProps> = ({
  rows,
  visibleIndexes,
  indexBySpanId,
  isNeutral,
  maxPct,
  rowHeight,
  markerHitWidth,
  markersByKey,
  laneErrors,
  activeSpanIndex,
  selectedSpanIndex,
  colorFor,
  route,
  tickIndexes,
  onEnter,
  onLeave,
  onSelect,
}) => {
  const { t } = useTranslation()

  return (
    <Stack direction="row" alignItems="flex-start">
      {/* Outside the grid, so it stays put as the window slides. */}
      {!isNeutral && (
        <Box sx={{ width: LABEL_WIDTH, flexShrink: 0 }}>
          {rows.map((row) => (
            <Box
              key={row.dataKey}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                height: rowHeight,
                mt: `${MARKER_BAND_HEIGHT}px`,
                mb: `${ROW_GAP}px`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.6rem",
                  fontWeight: 700,
                  color: "text.primary",
                  letterSpacing: "0.05em",
                }}
              >
                {t(row.labelKey)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          overflowX: "clip",
          overflowClipMargin: `${markerHitWidth}px`,
        }}
      >
        {rows.map((row) => {
          const laneKey = row.dataKey === "dlr" ? "dlr" : "aar"
          const hasLaneError = !isNeutral && Boolean(laneErrors?.[laneKey])
          const { peakIndex, currentIndex, isSplit, peakPct, currentTime } =
            getLaneMarkers(row, indexBySpanId, markersByKey?.[laneKey])

          return (
            <Box key={row.dataKey} sx={{ mb: `${ROW_GAP}px` }}>
              {!isNeutral && (
                <LaneMarkerBand
                  visibleIndexes={visibleIndexes}
                  peakIndex={peakIndex}
                  currentIndex={currentIndex}
                  isSplit={isSplit}
                  peakColor={colorFor(getIntensity(peakPct, maxPct))}
                  peakLabel={String(
                    t("spans.ampacity.markers.peak", { method: laneKey.toUpperCase() }),
                  )}
                  currentLabel={String(
                    t("spans.ampacity.markers.current", {
                      method: laneKey.toUpperCase(),
                    }),
                  )}
                  currentCaption={currentTime ? formatTimestamp(currentTime) : null}
                  hitWidth={markerHitWidth}
                />
              )}
              <Box
                sx={{
                  display: "flex",
                  // Cells must stretch: a bar is `height: 100%` of its cell.
                  alignItems: "stretch",
                  height: rowHeight,
                  bgcolor: "rgba(255,255,255,0.05)",
                  borderRadius: "3px",
                  // A failed method gets hatching instead of bars.
                  ...(hasLaneError
                    ? {
                        backgroundImage:
                          "repeating-linear-gradient(-45deg, rgba(255,255,255,0.06) 0 6px, transparent 6px 12px)",
                        alignItems: "center",
                        justifyContent: "center",
                      }
                    : null),
                }}
              >
                {hasLaneError && (
                  <Typography
                    variant="caption"
                    sx={{ fontSize: "0.6rem", color: "text.disabled" }}
                  >
                    {t("spans.ampacity.lane.unavailable", {
                      method: laneKey.toUpperCase(),
                    })}
                  </Typography>
                )}
                {!hasLaneError &&
                  visibleIndexes.map((index) => {
                    const pt = row.points[index]
                    if (!pt) return <GridCell key={`empty-${index}`} />
                    const intensity = getIntensity(pt.pct, maxPct)
                    const isHovered = pt.spanIndex === activeSpanIndex
                    const isSelected = pt.spanIndex === selectedSpanIndex

                    return (
                      <SpanCell
                        key={pt.spanIndex}
                        spanIndex={pt.spanIndex}
                        spanId={pt.spanId}
                        color={isNeutral ? route : colorFor(intensity)}
                        isHovered={isHovered}
                        isSelected={isSelected}
                        // Neutral is one colour, so the span you are on comes
                        // forward by everything else stepping back.
                        isDimmed={
                          !isHovered &&
                          !isSelected &&
                          (isNeutral || intensity <= LIMITING_INTENSITY_THRESHOLD)
                        }
                        onEnter={onEnter}
                        onLeave={onLeave}
                        onSelect={onSelect}
                      />
                    )
                  })}
              </Box>
            </Box>
          )
        })}

        <AxisRuler visibleIndexes={visibleIndexes} tickIndexes={tickIndexes} />
      </Box>
    </Stack>
  )
}

export default LaneStrips
